// src/components/production/DesvioMotivoModal.js
import React, { useState, useEffect, useMemo } from "react";
import { AlertCircle, Plus, X, Check, EyeOff } from "lucide-react";
import { desvioEventosService } from "../../services/desvioEventosService";
import { MONITORED_STATUSES } from "../../config/monitoredStatuses";
import { PRODUCTION_STATUS_CONFIG } from "../../config/productionStatuses";

export default function DesvioMotivoModal({
  isOpen,
  onClose,
  statusDestino,
  ordersList = [],
  onSave,
}) {
  const [problemas, setProblemas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Estados do formulário
  const [selectedProblemaId, setSelectedProblemaId] = useState("");
  const [observacoesGeral, setObservacoesGeral] = useState("");

  // Overrides para modo lote: { [orderId]: { problema_id, observacoes } }
  const [overrides, setOverrides] = useState({});

  // Estados de criação de novo motivo
  const [isCriandoMotivo, setIsCriandoMotivo] = useState(false);
  const [novoMotivoNome, setNovoMotivoNome] = useState("");
  const [novoMotivoCategoria, setNovoMotivoCategoria] = useState("");
  const [isNovaCategoria, setIsNovaCategoria] = useState(false);
  const [novaCategoriaNome, setNovaCategoriaNome] = useState("");

  const isLote = ordersList.length > 1;
  const statusConfig = MONITORED_STATUSES[statusDestino] || {};

  // Carrega motivos ao abrir
  const loadProblemas = async () => {
    if (!statusDestino) return;
    setLoading(true);
    try {
      const data = await desvioEventosService.getProblemas(statusDestino);
      setProblemas(data);

      // Pré-seleciona o "NÃO INFORMADO" se existir
      const naoInformado = data.find((p) => p.nome === "NÃO INFORMADO");
      if (naoInformado) {
        setSelectedProblemaId(naoInformado.id);
        
        // Inicializa overrides
        const initialOverrides = {};
        ordersList.forEach((order) => {
          initialOverrides[order.id] = {
            problema_id: naoInformado.id,
            observacoes: "",
          };
        });
        setOverrides(initialOverrides);
      } else if (data.length > 0) {
        setSelectedProblemaId(data[0].id);
      }
    } catch (e) {
      console.error("Erro ao carregar catálogo de desvios:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && statusDestino) {
      loadProblemas();
      setIsCriandoMotivo(false);
      setNovoMotivoNome("");
      setNovoMotivoCategoria("");
      setIsNovaCategoria(false);
      setNovaCategoriaNome("");
      setObservacoesGeral("");
    }
  }, [isOpen, statusDestino, ordersList.length]);

  // Lista de categorias únicas para o formulário de cadastro inline
  const categoriasUnicas = useMemo(() => {
    const cats = problemas.map((p) => p.expand?.categoria?.nome).filter(Boolean);
    return Array.from(new Set(cats));
  }, [problemas]);

  // Agrupa os problemas por categoria para renderizar optgroups
  const groupedProblemas = useMemo(() => {
    const groups = {};
    problemas.forEach((p) => {
      const catName = p.expand?.categoria?.nome || "GERAL";
      if (!groups[catName]) groups[catName] = [];
      groups[catName].push(p);
    });
    return groups;
  }, [problemas]);

  // Motivo selecionado no momento (individual)
  const selectedProblema = useMemo(() => {
    return problemas.find((p) => p.id === selectedProblemaId);
  }, [problemas, selectedProblemaId]);

  if (!isOpen) return null;

  // Lógica de Override Lote
  const handleOverrideReasonChange = (orderId, problemaId) => {
    setOverrides((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        problema_id: problemaId,
      },
    }));
  };

  const handleOverrideObsChange = (orderId, obs) => {
    setOverrides((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        observacoes: obs,
      },
    }));
  };

  const handleGeneralReasonChange = (problemaId) => {
    setSelectedProblemaId(problemaId);
    setOverrides((prev) => {
      const updated = { ...prev };
      ordersList.forEach((order) => {
        updated[order.id] = {
          ...updated[order.id],
          problema_id: problemaId,
        };
      });
      return updated;
    });
  };

  const handleGeneralObsChange = (obs) => {
    setObservacoesGeral(obs);
    setOverrides((prev) => {
      const updated = { ...prev };
      ordersList.forEach((order) => {
        updated[order.id] = {
          ...updated[order.id],
          observacoes: obs,
        };
      });
      return updated;
    });
  };

  // Salvar motivo criado inline
  const handleSaveNovoMotivo = async (e) => {
    e.preventDefault();
    if (!novoMotivoNome.trim()) return;

    const catFinal = isNovaCategoria ? novaCategoriaNome : novoMotivoCategoria;
    if (!catFinal.trim()) return;

    try {
      const novoProblema = await desvioEventosService.createProblema(
        novoMotivoNome,
        catFinal,
        statusDestino
      );

      // Adiciona ao estado local e seleciona
      setProblemas((prev) => [...prev, novoProblema]);
      setSelectedProblemaId(novoProblema.id);

      if (isLote) {
        setOverrides((prev) => {
          const updated = { ...prev };
          ordersList.forEach((order) => {
            updated[order.id] = {
              ...updated[order.id],
              problema_id: novoProblema.id,
            };
          });
          return updated;
        });
      }

      // Limpa formulário
      setNovoMotivoNome("");
      setNovoMotivoCategoria("");
      setNovaCategoriaNome("");
      setIsNovaCategoria(false);
      setIsCriandoMotivo(false);
    } catch (err) {
      alert("Erro ao criar novo motivo: " + err.message);
    }
  };

  // Envio final do formulário
  const handleSubmit = (e) => {
    e.preventDefault();

    if (isLote) {
      // Valida obrigatoriedade de observações por linha em lote
      for (const order of ordersList) {
        const ov = overrides[order.id] || {};
        const prob = problemas.find((p) => p.id === ov.problema_id);
        if (prob?.obs_obrigatoria && !ov.observacoes?.trim()) {
          alert(`O item ${order.sku} está com o motivo "${prob.nome}" que exige observações obrigatórias!`);
          return;
        }
      }

      const eventos = ordersList.map((order) => {
        const ov = overrides[order.id] || {};
        return {
          status_destino: statusDestino,
          order_id: order.id,
          sku: order.sku,
          order_number: order.order?.number || "AVULSO",
          status_anterior: order.status || "",
          problema_id: ov.problema_id,
          observacoes: ov.observacoes || "",
          preenchido: true,
        };
      });

      onSave(eventos);
    } else {
      // Valida obrigatoriedade de observações individual
      if (selectedProblema?.obs_obrigatoria && !observacoesGeral.trim()) {
        alert("Este motivo exige que você descreva as observações!");
        return;
      }

      const order = ordersList[0];
      const evento = {
        status_destino: statusDestino,
        order_id: order.id,
        sku: order.sku,
        order_number: order.order?.number || "AVULSO",
        status_anterior: order.status || "",
        problema_id: selectedProblemaId,
        observacoes: observacoesGeral,
        preenchido: true,
      };

      onSave([evento]);
    }
  };

  // Dispensar
  const handleDispensar = () => {
    const naoInformado = problemas.find((p) => p.nome === "NÃO INFORMADO");
    const problemaId = naoInformado ? naoInformado.id : "";

    const eventos = ordersList.map((order) => ({
      status_destino: statusDestino,
      order_id: order.id,
      sku: order.sku,
      order_number: order.order?.number || "AVULSO",
      status_anterior: order.status || "",
      problema_id: problemaId,
      observacoes: "",
      preenchido: false,
    }));

    onSave(eventos);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-slate-100 animate-scale-up overflow-hidden">
        
        {/* CABEÇALHO */}
        <div className="bg-slate-900 text-white p-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-red-500/20 text-red-400 p-2 rounded-lg">
              <AlertCircle size={22} />
            </div>
            <div>
              <h3 className="font-bold text-lg">Controle de Qualidade</h3>
              <p className="text-slate-400 text-xs mt-0.5">
                {statusConfig.titulo_modal || "Coleta de motivo de desvio"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* CORPO DO MODAL */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* MODO INLINE DE CRIAR MOTIVO */}
          {isCriandoMotivo ? (
            <form onSubmit={handleSaveNovoMotivo} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm text-slate-700">Adicionar Novo Motivo</span>
                <button
                  type="button"
                  onClick={() => setIsCriandoMotivo(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  Cancelar
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                    Nome do Motivo:
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: GARRA QUEBRADA"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-slate-800 outline-none uppercase"
                    value={novoMotivoNome}
                    onChange={(e) => setNovoMotivoNome(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                    Estágio / Categoria:
                  </label>
                  {!isNovaCategoria ? (
                    <div className="flex gap-2">
                      <select
                        required
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:border-slate-800 outline-none"
                        value={novoMotivoCategoria}
                        onChange={(e) => {
                          if (e.target.value === "NEW") {
                            setIsNovaCategoria(true);
                            setNovoMotivoCategoria("");
                          } else {
                            setNovoMotivoCategoria(e.target.value);
                          }
                        }}
                      >
                        <option value="" disabled>Selecione...</option>
                        {categoriasUnicas.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                        <option value="NEW" className="text-blue-600 font-bold">+ Nova Categoria...</option>
                      </select>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        required
                        placeholder="Ex: CRAVAÇÃO"
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-slate-800 outline-none uppercase"
                        value={novaCategoriaNome}
                        onChange={(e) => setNovaCategoriaNome(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIsNovaCategoria(false);
                          setNovaCategoriaNome("");
                        }}
                        className="text-xs text-slate-500 hover:text-slate-800 border border-slate-300 rounded-lg px-2"
                      >
                        Voltar
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="submit"
                  className="bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-slate-850 flex items-center gap-1.5"
                >
                  <Check size={14} /> Salvar e Selecionar
                </button>
              </div>
            </form>
          ) : (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setIsCriandoMotivo(true)}
                className="text-blue-600 hover:text-blue-800 text-xs font-bold flex items-center gap-1 border border-blue-200 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Plus size={14} /> + Novo Motivo
              </button>
            </div>
          )}

          {/* FORMULÁRIO DE SELEÇÃO */}
          {loading ? (
            <div className="py-12 text-center text-slate-500 text-sm">
              Carregando catálogo de motivos...
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* MODO LOTE */}
              {isLote ? (
                <div className="space-y-6">
                  
                  {/* CONFIG GERAL LOTE */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <span className="block text-xs font-bold text-slate-500 uppercase mb-3">
                      Preenchimento Geral do Lote ({ordersList.length} itens)
                    </span>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">Motivo Geral:</label>
                        <select
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:border-slate-800 outline-none"
                          value={selectedProblemaId}
                          onChange={(e) => handleGeneralReasonChange(e.target.value)}
                        >
                          {Object.keys(groupedProblemas).map((catName) => (
                            <optgroup key={catName} label={catName}>
                              {groupedProblemas[catName].map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.nome} {p.obs_obrigatoria ? "(Exige Obs)" : ""}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs text-slate-600 mb-1">Observações Geral:</label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-slate-800 outline-none"
                          placeholder="Observação copiada para todas as linhas..."
                          value={observacoesGeral}
                          onChange={(e) => handleGeneralObsChange(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* TABELA DE OVERRIDES INDIVIDUAIS POR LINHA */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm max-h-[300px] overflow-y-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider sticky top-0">
                        <tr>
                          <th className="p-3 border-b">Pedido / SKU</th>
                          <th className="p-3 border-b">De Etapa</th>
                          <th className="p-3 border-b">Motivo Individual</th>
                          <th className="p-3 border-b">Observação Individual</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {ordersList.map((order) => {
                          const ov = overrides[order.id] || {};
                          const currentProb = problemas.find((p) => p.id === ov.problema_id);
                          const isRequired = currentProb?.obs_obrigatoria;
                          
                          const oldConfig = PRODUCTION_STATUS_CONFIG[order.status] || {};
                          const oldLabel = oldConfig.label || order.status || "Status Anterior";

                          return (
                            <tr key={order.id} className="hover:bg-slate-50">
                              <td className="p-3 font-bold text-slate-800">
                                <div>{order.sku}</div>
                                <div className="text-[10px] text-slate-400 font-normal">
                                  Nº {order.order?.number || "AVULSO"}
                                </div>
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${oldConfig.color || "bg-slate-150 text-slate-700"}`}>
                                  {oldLabel}
                                </span>
                              </td>
                              <td className="p-3">
                                <select
                                  className="w-full px-2 py-1 border border-slate-300 rounded-lg text-xs bg-white outline-none focus:border-slate-800"
                                  value={ov.problema_id || ""}
                                  onChange={(e) => handleOverrideReasonChange(order.id, e.target.value)}
                                >
                                  {Object.keys(groupedProblemas).map((catName) => (
                                    <optgroup key={catName} label={catName}>
                                      {groupedProblemas[catName].map((p) => (
                                        <option key={p.id} value={p.id}>
                                          {p.nome}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                              </td>
                              <td className="p-3">
                                <input
                                  type="text"
                                  className={`w-full px-2 py-1 border rounded-lg text-xs outline-none focus:border-slate-800 ${
                                    isRequired && !ov.observacoes?.trim()
                                      ? "border-red-400 bg-red-50/30 placeholder-red-400"
                                      : "border-slate-300"
                                  }`}
                                  placeholder={isRequired ? "Obrigatório..." : "Opcional..."}
                                  value={ov.observacoes || ""}
                                  onChange={(e) => handleOverrideObsChange(order.id, e.target.value)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                </div>
              ) : (
                
                // MODO INDIVIDUAL
                <div className="space-y-5 max-w-xl mx-auto">
                  {ordersList[0] && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex justify-between items-center text-xs">
                      <div>
                        <span className="text-slate-400 block uppercase font-bold text-[10px]">Item Selecionado</span>
                        <span className="text-sm font-bold text-slate-800">{ordersList[0].sku}</span>
                        <span className="text-slate-500 ml-2">({ordersList[0].order?.number ? `Pedido: ${ordersList[0].order.number}` : "Avulso"})</span>
                      </div>

                      <div className="text-right">
                        <span className="text-slate-400 block uppercase font-bold text-[10px]">Origem</span>
                        <span className={`px-2 py-0.5 rounded font-bold ${
                          PRODUCTION_STATUS_CONFIG[ordersList[0].status]?.color || "bg-slate-200 text-slate-800"
                        }`}>
                          {PRODUCTION_STATUS_CONFIG[ordersList[0].status]?.label || ordersList[0].status}
                        </span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                      Motivo Principal:
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:border-slate-800 outline-none"
                      value={selectedProblemaId}
                      onChange={(e) => setSelectedProblemaId(e.target.value)}
                    >
                      {Object.keys(groupedProblemas).map((catName) => (
                        <optgroup key={catName} label={catName}>
                          {groupedProblemas[catName].map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.nome} {p.obs_obrigatoria ? " (Exige observações)" : ""}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                      Observações {selectedProblema?.obs_obrigatoria && <span className="text-red-500">*</span>}:
                    </label>
                    <textarea
                      rows="3"
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:border-slate-800 outline-none ${
                        selectedProblema?.obs_obrigatoria && !observacoesGeral.trim()
                          ? "border-red-400 bg-red-50/20"
                          : "border-slate-300"
                      }`}
                      placeholder={
                        selectedProblema?.obs_obrigatoria
                          ? "Descreva os detalhes específicos deste erro (obrigatório para este motivo)..."
                          : "Observações adicionais (opcional)..."
                      }
                      value={observacoesGeral}
                      onChange={(e) => setObservacoesGeral(e.target.value)}
                    ></textarea>
                    {selectedProblema?.obs_obrigatoria && (
                      <span className="text-[10px] text-red-500 font-bold block mt-1 flex items-center gap-1">
                        <AlertCircle size={10} /> O motivo "{selectedProblema.nome}" exige observações detalhadas.
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* AÇÕES FINAIS */}
              <div className="flex items-center justify-between border-t border-slate-100 pt-5 shrink-0">
                <button
                  type="button"
                  onClick={handleDispensar}
                  className="text-slate-500 hover:text-slate-800 text-xs font-bold py-2.5 px-4 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-1.5"
                >
                  <EyeOff size={16} /> Dispensar Formulário
                </button>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="border border-slate-300 text-slate-700 text-xs font-bold py-2.5 px-5 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Fechar
                  </button>
                  <button
                    type="submit"
                    className="bg-slate-900 text-white text-xs font-bold py-2.5 px-6 rounded-lg hover:bg-slate-850 transition-colors shadow-md"
                  >
                    Gravar e Mudar Status
                  </button>
                </div>
              </div>

            </form>
          )}

        </div>

      </div>
    </div>
  );
}
